import React, { useEffect, useState } from 'react';
import { Form, Input, message, Modal, Select, Upload } from 'antd';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { SystemUser } from 'interface/Personal';
import { AppState } from 'interface/stateApp';
import { ROLE_NAME } from 'utils/data';
import { uploadFile } from 'service/fileUploadService';
import { createSysUsers, updateUser } from 'service/userService';
import { isValid } from 'utils/helper';

const { Option } = Select;

interface CreateOrEditUserModalProps {
  user?: SystemUser;
  stateApp: AppState;
  onClose?: any;
  onData?: () => void;
  key?: any;
}

const CreateOrEditUserModal = ({ user, onData, key, stateApp, onClose }: CreateOrEditUserModalProps) => {
  const [visible, setVisible] = useState(true); // State for modal visibility
  const [form] = Form.useForm(); // Ant Design form instance
  const { showCreateUserForm } = stateApp;
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string>();
  // Handle form submission
  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        onSubmit(values);
      })
      .catch((info) => {
        message.error('Vui lòng nhập đủ thông tin!');
      });
  };

  const onSubmit = (values: any) => {
    if (values?.password !== values.repassword && !user) {
      message.error('Mật khẩu nhập lại không khớp');
      return;
    }
    if (!user) {
      createSysUsers(values)
        .then((response) => {
          message.success('Tạo người dùng thành công!');
          if (onData) {
            onData();
          }
          onClose();
        })
        .catch((err) => {
          console.log(err?.response?.data);
          message.error(err.response?.data?.message);
        });
    } else {
      updateUser(user?.id, values)
        .then((response) => {
          message.success('Cập nhật thông tin người dùng thành công!');
          if (onData) {
            onData();
          }
          onClose();
        })
        .catch((err) => {
          console.log(err?.response?.data);
          message.error(err.response?.data?.message);
        });
    }
  };

  const uploadButton = (
    <button style={{ border: 0, background: 'none' }} type="button">
      {loading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>Upload</div>
    </button>
  );

  const handleFileChange = (info: any) => {
    const file = info.file;
    if (file) {
      const isImage = info.file.type.startsWith('image/');
      if (!isImage) {
        message.error('Vui lòng tải ảnh!');
        return;
      }

      const formData = new FormData();
      formData.append('file', file);
      setLoading(true);
      uploadFile(formData)
        .then((data) => {
          if (data?.status === 1) {
            form.setFieldsValue({ imageUrl: data?.url });
            setImageUrl(data?.url);
          } else {
            message.error('Upload image thất bại, vui lòng thử lại.');
          }
        })
        .catch((error) => {
          message.error('Upload image thất bại, vui lòng thử lại.');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      message.error('Vui lòng tải lại ảnh!');
    }
  };

  useEffect(() => {
    console.log(user);
    if (!user) {
      form.resetFields();
      setImageUrl('');
    } else {
      form.setFieldsValue(user);
      setImageUrl(user?.imageUrl);
    }
  }, [user, key]);

  return (
    <>
      <Modal
        title={user ? 'Cập nhật thông tin' : 'Thêm người dùng'}
        visible={showCreateUserForm}
        onOk={handleOk}
        onCancel={onClose}
        okText={user ? 'Cập nhật' : 'Tạo'}
        cancelText="Cancel"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={user || { active: true }} // Default form values
        >
          <Form.Item
            label="First Name"
            name="firstName"
            rules={[{ required: true, message: 'Vui lòng nhập first name' }]}
          >
            <Input placeholder="Nhập first name" />
          </Form.Item>

          <Form.Item label="Last Name" name="lastName" rules={[{ required: true, message: 'Vui lòng nhập last name' }]}>
            <Input placeholder="Nhập last name" />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[{ required: true, type: 'email', message: 'Vui lòng nhập email' }]}
          >
            <Input placeholder="Nhập email" disabled={isValid(user)} />
          </Form.Item>

          <Form.Item
            label="Mật khẩu"
            name="password"
            rules={[{ required: !isValid(user), message: 'Vui lòng nhập mật khẩu' }]}
          >
            <Input.Password placeholder="Nhập password" />
          </Form.Item>

          <Form.Item
            label="Nhập lại mật khẩu"
            name="repassword"
            rules={[{ required: !isValid(user), message: 'Vui lòng nhập lại mật khẩu' }]}
          >
            <Input.Password placeholder="Nhập lại mật khẩu" />
          </Form.Item>

          <Form.Item label="Role" name="role" rules={[{ required: true, message: 'Vui lòng chọn role' }]}>
            <Select placeholder="Chọn role">
              <Option value={ROLE_NAME.ROLE_CTV}>Công tác viên</Option>
              <Option value={ROLE_NAME.ROLE_EXPERT}>Chuyên gia</Option>
              <Option value={ROLE_NAME.ROLE_STUDENT}>Học sinh</Option>
              <Option value={ROLE_NAME.ROLE_COMPILER}>Biên tập</Option>
              <Option value={ROLE_NAME.ROLE_MASTER_COMPILER}>Tổng biên tập</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Ảnh đại diện" name="imageUrl" valuePropName="file">
            <Upload
              accept="image/*"
              name="imageUrl"
              listType="picture-card"
              beforeUpload={() => false}
              maxCount={1}
              showUploadList={false}
              onChange={(info) => handleFileChange(info)}
            >
              {imageUrl ? <img src={imageUrl} alt="avatar" style={{ width: '100%' }} /> : uploadButton}
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CreateOrEditUserModal;
